// @ts-check
// Botão que abre a lista de opções com ícone, título e descrição (spec 2.0.0, fluxos.md seção 20.7: "Devem percorrer o
// fluxo" e "Selecione a entrada"): o botão mostra a opção escolhida (ou o convite), a lista é um listbox com setas,
// Enter e Esc, e o foco volta para o botão.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { Dropdown } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';

/**
 * @typedef {{ value: string, title: string, description?: string | null, icon?: string | null, disabled?: boolean, hint?: string | null }} FlowChoiceOption
 */

/**
 * @param {{ options: FlowChoiceOption[], value: string | null, placeholder: string, onChange: (value: string) => void, ariaLabel: string,
 *   icon?: string | null, width?: number, testId?: string, invalid?: boolean }} props
 */
export function FlowChoiceMenu({ options, value, placeholder, onChange, ariaLabel, icon = null, width = 340, testId, invalid = false }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const listRef = useRef(/** @type {HTMLUListElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const current = options.find((option) => option.value === value) ?? null;
  const listId = useRef(`flow-choice-${Math.random().toString(36).slice(2, 9)}`).current;

  const focusOption = (/** @type {number} */ index) => {
    const items = /** @type {HTMLElement[]} */ ([...(listRef.current?.querySelectorAll('[role="option"]:not([aria-disabled="true"])') ?? [])]);
    if (items.length > 0) items[(index + items.length) % items.length].focus();
  };
  const open = () => {
    setIsOpen(true);
    requestAnimationFrame(() => {
      const items = /** @type {HTMLElement[]} */ ([...(listRef.current?.querySelectorAll('[role="option"]:not([aria-disabled="true"])') ?? [])]);
      const selectedIndex = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
      focusOption(Math.max(0, selectedIndex));
    });
  };
  const close = () => {
    setIsOpen(false);
    requestAnimationFrame(() => anchorRef.current?.focus({ preventScroll: true }));
  };
  /** @param {FlowChoiceOption} option */
  const choose = (option) => {
    if (option.disabled) return;
    onChange(option.value);
    close();
  };

  return html`<button type="button" ref=${anchorRef} class="flow-choice-button" aria-haspopup="listbox" aria-expanded=${isOpen ? 'true' : 'false'}
      aria-controls=${isOpen ? listId : undefined} aria-label=${`${ariaLabel}: ${current?.title ?? placeholder}`} data-testid=${testId}
      data-empty=${current ? undefined : 'true'} data-invalid=${invalid ? 'true' : undefined}
      onClick=${() => (isOpen ? close() : open())}
      onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isOpen) {
          event.preventDefault();
          open();
        }
      }}>
      ${current?.icon || icon ? html`<${Icon} name=${current?.icon ?? icon ?? 'IconChevronDown'} size=${16} />` : null}
      <span class="flow-choice-button-text">${current?.title ?? placeholder}</span>
      <${Icon} name="IconChevronDown" size=${14} />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${close} placement="bottom-start" width=${width} role="presentation">
      <ul class="flow-choice-list" role="listbox" id=${listId} aria-label=${ariaLabel} ref=${listRef}
        onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
          const items = /** @type {HTMLElement[]} */ ([...(listRef.current?.querySelectorAll('[role="option"]:not([aria-disabled="true"])') ?? [])]);
          const index = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            focusOption(index + 1);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            focusOption(index - 1);
          } else if (event.key === 'Tab') {
            setIsOpen(false);
          }
        }}>
        ${options.map((option) => html`<li key=${option.value} role="option" class="flow-choice-option" tabindex="-1"
          aria-selected=${option.value === value ? 'true' : 'false'} aria-disabled=${option.disabled ? 'true' : undefined} title=${option.hint ?? undefined}
          onClick=${() => choose(option)}
          onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              choose(option);
            }
          }}>
          ${option.icon ? html`<span class="flow-choice-option-icon" aria-hidden="true"><${Icon} name=${option.icon} size=${18} /></span>` : null}
          <span class="flow-choice-option-texts">
            <span class="flow-choice-option-title">${option.title}</span>
            ${option.description ? html`<span class="flow-choice-option-description">${option.description}</span>` : null}
            ${option.disabled && option.hint ? html`<span class="flow-choice-option-description">${option.hint}</span>` : null}
          </span>
          ${option.value === value ? html`<${Icon} name="IconCheck" size=${16} className="flow-choice-option-check" />` : null}
        </li>`)}
      </ul>
    <//>`;
}
